import {
	Component,
	OnInit,
	ViewChild,
	ElementRef,
	HostListener,
	AfterViewInit,
	Input,
	OnChanges,
	Renderer2,
	ChangeDetectorRef,
	OnDestroy,
	EventEmitter,
	Output
} from '@angular/core';
import { fade } from '@app/system/animations/side-nav-slide.animate';
import { DetectDeviceService } from '@app/system/services/detect-device.service';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { trigger, style, transition, animate, group, state, query, animateChild } from '@angular/animations';
import { GlobalFunctionService } from '@app/system/services/global-function.service';

export interface VideoPlayerOptions {
	skip?: boolean;
	fullscreen?: boolean;
	bookmarks?: Array<{
		key: string;
		caption: string;
		timeline: Array<string>;
	}>;
}

export interface TrackingEmit {
	eventType?: string;
	time?: string;
	isPlayed?: boolean
}

export const playPauseTransition = trigger('playPauseTransition', [
	transition(':enter', [
		group([
			animate(
				'0ms 500ms ease',
				style({
					transform: 'scale(2)',
					opacity: '0',
					visibility: 'hidden'
				})
			)
		])
	])
]);

export const skipTransition = trigger('skipTransition', [
	transition(':enter', [
		group([
			animate(
				'0ms 500ms ease',
				style({
					opacity: '0',
					visibility: 'hidden'
				})
			)
		])
	])
]);

export const accordionY = trigger('accordionY', [
	state(
		'on',
		style({
			'max-height': '*',
			overflow: '*',
		})
	),
	state(
		'off',
		style({
			'max-height': '35px',
			overflow: 'hidden',
		})
	),
	transition('on => off', [
		group([
			animate(
				'10ms ease',
				style({
					overflow: 'hidden',
					'max-height': '0px'
				})
			)
		])
	]),
	transition('off => on', [
		group([
			query('@*', [animateChild()], { optional: true }),
			animate(
				'10ms ease',
				style({
					'max-height': '*',
					overflow: '*'
				})
			)
		])
	])
]);

@Component({
	selector: 'app-video-player',
	templateUrl: './video-player.component.html',
	styleUrls: ['./video-player.component.scss'],
	animations: [fade, playPauseTransition, skipTransition, accordionY]
})
export class VideoPlayerComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

	@Input() source: string;
	@Input() options: VideoPlayerOptions;
	@Input() trackingEmit: Array<TrackingEmit> = [];
	@Input() thumbnail: string;
	@Input() currentTime: string;
	@Input() canStartPlay: boolean = false;
	@Input() segmentsProcess: boolean = false;

	@Output() timeEmit = new EventEmitter();
	@Output() segments = new EventEmitter();
	@Output() startPlaying = new EventEmitter();
	@Output() updatedTime = new EventEmitter();
	@ViewChild('videoEl')
	videoEl: ElementRef;
	videoNativeEl;
	@ViewChild('videoContainer') videoContainer: ElementRef;
	@ViewChild('videoBlock') videoBlock: ElementRef;
	@ViewChild('seekBarEl') seekBarEl: ElementRef;
	seekBarNativeEl;
	@ViewChild('playerProgressEl') playerProgressEl: ElementRef;
	playerProgressNativeEl;
	@ViewChild('playerPointerEl', { static: false })
	playerPointerEl: ElementRef;
	playerPointerNativeEl: HTMLElement;
	@ViewChild('bufferEl') bufferEl: ElementRef;
	bufferNativeEl;
	@ViewChild('progressTooltipEl') progressTooltipEl: ElementRef;
	progressTooltipNativeEl;
	@ViewChild('volumeControlEl') volumeControlEl: ElementRef;
	volumeControlNativeEl;
	@ViewChild('volumeBarEl') volumeBarEl: ElementRef;
	volumeBarNativeEl;
	@ViewChild('volumeProgressEl') volumeProgressEl: ElementRef;
	volumeProgressNativeEl;
	@ViewChild('volumePointerEl') volumePointerEl: ElementRef;
	volumePointerNativeEl;
	@ViewChild('bookmarkEl') bookmarkEl: ElementRef;
	bookmarkNativeEl;

	isPlaying = false;
	videoElapsedTime = '00:00';
	fullscreen = false;
	duration: any = '00:00';
	hideControl = false;
	timeout;
	loader = false;
	defaultOptions: VideoPlayerOptions = {
		fullscreen: true,
		skip: true
	};
	doubleClicked = false;
	preventSimpleClick = false;
	clickTimeout;
	playStatus = false;
	currentVolume = 1;
	showVolumeBar = false;
	isDesktop = false;
	playerPointerBounds = null;
	seekBarBounds = null;
	volumeBarBounds = null;

	documentListenMM;
	documentListenMU;

	dragState = false;
	progressBarState = false;
	volumeDragState = false;
	volumeBarState = false;
	subscribe;
	hidePlayPause = true;
	hideSkipForward = true;
	hideSkipBackward = true;
	playPauseTimeout;
	touchCount = 0;
	showExtraBookmark = false;
	expanded = false;
	bookMarksData: any = {};
	currentBookmarkIndex = 0;
	showBookmarkPagination = false;
	currentTimelineIndex = 0;
	totalDuration;
	loaded = false;
	canPlay = false;
	segmentSplitValue = 30;
	totalSegments = 0;
	segmentData = [];
	segmentedData: Array<Number> = [];
	segmentedExpandedData = [];
	oldSegmentIndex;
	deviceDetails: any;

	constructor(
		private hostElement: ElementRef,
		private renderer2: Renderer2,
		private cdref: ChangeDetectorRef,
		private detectDevice: DetectDeviceService,
		private iconRegistry: MatIconRegistry,
		private sanitizer: DomSanitizer,
		public gfService: GlobalFunctionService
	) {
		iconRegistry.addSvgIcon('forward', sanitizer.bypassSecurityTrustResourceUrl('/assets/svg/forward.svg'));
		iconRegistry.addSvgIcon('rewind', sanitizer.bypassSecurityTrustResourceUrl('/assets/svg/rewind.svg'));
		if (this.detectDevice.resolutionState$.value.deviceType !== 'desktop') {
			this.hidePlayPause = false;
		}
		this.subscribe = this.detectDevice.resolutionState$.subscribe((device) => {
			this.isDesktop = device.isDesktop;
			this.deviceDetails = device;
			if (this.playerProgressNativeEl) {
				this.playerPointerBounds = this.playerPointerNativeEl.getBoundingClientRect();
				const playerProgressBounds = this.playerProgressNativeEl.getBoundingClientRect();
				this.renderer2.setStyle(
					this.playerPointerNativeEl,
					'transform',
					`translateX(${playerProgressBounds.width}px)`
				);
			}
			if (this.seekBarNativeEl) {
				this.seekBarBounds = this.seekBarNativeEl.getBoundingClientRect();
			}
			if (this.volumeControlNativeEl) {
				this.volumeBarBounds = this.volumeControlNativeEl.getBoundingClientRect();
			}
		});
	}

	loadVideo() {
		if (this.canStartPlay && navigator.onLine) {
			this.startPlaying.emit(true);
			this.loader = true;
			this.loaded = true;
			this.cdref.detectChanges();
			this.volumeControlNativeEl = this.volumeControlEl.nativeElement;
			this.volumeBarNativeEl = this.volumeBarEl.nativeElement;
			this.volumeBarBounds = this.volumeBarNativeEl.getBoundingClientRect();
			this.volumePointerNativeEl = this.volumePointerEl.nativeElement;
			this.volumeProgressNativeEl = this.volumeProgressEl.nativeElement;
			this.renderer2.setStyle(this.volumeProgressNativeEl, 'height', `${this.volumeBarNativeEl.offsetHeight}px`);
			this.renderer2.setStyle(
				this.volumePointerNativeEl,
				'transform',
				`translateY(-${this.volumeBarNativeEl.offsetHeight}px)`
			);
			this.videoNativeEl = this.videoEl.nativeElement;
			this.seekBarNativeEl = this.seekBarEl.nativeElement;
			this.seekBarBounds = this.seekBarNativeEl.getBoundingClientRect();
			this.playerProgressNativeEl = this.playerProgressEl.nativeElement;
			this.playerPointerNativeEl = <HTMLElement>this.playerPointerEl.nativeElement;
			this.playerPointerBounds = this.playerPointerNativeEl.getBoundingClientRect();
			this.bufferNativeEl = this.bufferEl.nativeElement;
			this.progressTooltipNativeEl = this.progressTooltipEl.nativeElement;
			this.videoNativeEl.addEventListener('timeupdate', this.updateTimeElapsed.bind(this));
			this.videoNativeEl.addEventListener('progress', this.bufferHandler.bind(this));
			this.videoNativeEl.addEventListener('loadedmetadata', this.videoCanPlay.bind(this));
			this.videoNativeEl.addEventListener('canplay', this.loading.bind(this));
			this.videoNativeEl.addEventListener('ended', this.ended.bind(this));
			this.videoNativeEl.volume = 1;
			if (!this.currentTime) {
				if (!this.isDesktop) {
					this.togglePlayForMobile('icon');
				} else {
					this.videoNativeEl.play();
					this.isPlaying = true;
					this.timeoutManage();
				}
			}
		}
	}

	@HostListener("document:fullscreenchange", [])
	fullScreen() {
		if (navigator.onLine) {
			if (!document.fullscreenElement) {
				const el: HTMLElement = this.videoContainer.nativeElement;
				if (el.classList.contains('fullscreen-mode')) {
					const mainEl: HTMLElement = window.document.getElementById('main-container');
					if (mainEl) {
						this.renderer2.setStyle(mainEl, 'min-width', 'auto');
						this.renderer2.setStyle(mainEl, 'max-width', '100%');
					}
					if (!this.isDesktop && screen && screen.orientation) {
						this.gfService.globalBS$.next(
							{
								type: 'FULL_SCREEN',
								value: false
							}
						);
						screen.orientation.unlock();
					}
					this.hideControl = false;
					this.fullscreen = false;
				}
			}
		}
	}

	ended() {
		this.loader = false;
	}

	ngOnInit(): void {
		this.options = Object.assign(this.defaultOptions, this.options);
	}

	ngOnChanges(changes) {
		if (changes) {
			if (changes.hasOwnProperty('options')) {
				this.options = Object.assign(this.defaultOptions, changes.options.currentValue);
			}
			if (changes.hasOwnProperty('trackingEmit')) {
				for (const timeData of this.trackingEmit) {
					timeData.isPlayed = false;
				}
			}
		}
	}

	ngAfterViewInit() {
		if (this.currentTime && navigator.onLine) {
			setTimeout(() => {
				this.loadVideo();
			});
		}
	}

	showMoreBookmarks() {
		this.expanded = !this.expanded;
	}

	loading() {
		this.loader = false;
	}

	videoCanPlay() {
		if (navigator.onLine) {
			this.loader = false;
			this.canPlay = true;
			this.totalDuration = this.formatTime(Math.round(this.videoNativeEl.duration));
			if (this.segmentsProcess) {
				this.totalSegments = Math.round(this.videoNativeEl.duration / this.segmentSplitValue);
				if (this.totalSegments) {
					for (let i = 1; i <= this.totalSegments; i++) {
						let start = (i - 1) * this.segmentSplitValue;
						let end = i * this.segmentSplitValue;
						if (i === this.totalSegments) {
							end = this.videoNativeEl.duration;
						}
						this.segmentData.push(
							{
								start,
								end
							}
						);
					}
				}
			}
			if (this.currentTime) {
				this.videoNativeEl.currentTime = this.gfService.getTimeToSeconds(this.currentTime, { timeFormat: 'hh:mm:ss' });
			}
			if (!this.bookMarksData.length && this.options.bookmarks && this.options.bookmarks.length) {
				this.bookMarksData = this.options.bookmarks;
				this.cdref.detectChanges();
				if (this.bookmarkEl) {
					this.bookmarkNativeEl = this.bookmarkEl.nativeElement;
					if (this.bookmarkNativeEl.offsetHeight > 35) {
						this.showExtraBookmark = true;
					}
				}
				for (const data of this.bookMarksData) {
					data.timelineData = [];
					for (const timeline of data.timeline) {
						const duration = this.hmsToSecondsOnly(timeline);
						const pushJson = {
							duration,
							progress: (duration / this.videoNativeEl.duration) * 100
						};
						data.timelineData.push(pushJson);
					}
				}
			}
		}
	}

	hmsToSecondsOnly(str) {
		var p = str.split(':'),
			s = 0, m = 1;

		while (p.length > 0) {
			s += m * parseInt(p.pop(), 10);
			m *= 60;
		}

		return s;
	}

	setBookmarkTime(data) {
		if (navigator.onLine) {
			if (navigator.onLine) {
				if (data) {
					this.loader = true;
					const video = this.videoNativeEl;
					video.currentTime = data.duration;
					const time = this.formatTime(video.currentTime);
					this.videoElapsedTime = time;
					this.skip(video);
				}
			}
		}
	}

	changeBookmark(action: 'before' | 'after') {
		if (navigator.onLine) {
			if (action === 'after' && this.currentTimelineIndex !== this.bookMarksData[this.currentBookmarkIndex].timelineData.length - 1) {
				this.currentTimelineIndex += 1;
				this.setBookmarkTime(this.bookMarksData[this.currentBookmarkIndex].timelineData[this.currentTimelineIndex]);
			} else if (action === 'before' && this.currentTimelineIndex !== 0) {
				this.currentTimelineIndex -= 1;
				this.setBookmarkTime(this.bookMarksData[this.currentBookmarkIndex].timelineData[this.currentTimelineIndex]);
			}
		}
	}

	removeBookMarkSelection(data) {
		this.currentBookmarkIndex = 0;
		this.showBookmarkPagination = false;
	}

	transitDone() {
		if (this.isDesktop) {
			this.hidePlayPause = true;
		}
	}

	skipForwardTransitDone() {
		if (!this.isDesktop) {
			this.hideSkipForward = true;
		}
	}

	skipBackwardTransitDone() {
		if (!this.isDesktop) {
			this.hideSkipBackward = true;
		}
	}

	togglePlay(action: 'video' | 'icon') {
		if (navigator.onLine) {
			if (this.loaded) {
				const video = this.videoNativeEl;
				if (this.isDesktop) {
					this.preventSimpleClick = false;
					clearTimeout(this.timeout);
					this.clickTimeout = setTimeout(() => {
						if (!this.preventSimpleClick) {
							if (video.paused || video.ended) {
								this.isPlaying = true;
								// this.renderer2.setStyle(this.videoBlock.nativeElement, 'cursor', 'pointer');
								if (action === 'video') {
									this.timeoutManage();
								}
								video.play();
							} else {
								this.isPlaying = false;
								// this.renderer2.setStyle(this.videoBlock.nativeElement, 'cursor', 'pointer');
								this.hideControl = false;
								clearTimeout(this.timeout);
								video.pause();
							}
							if (action === 'video') {
								this.hidePlayPause = false;
							}
						}
					}, 200);
				} else {
					if (action === 'video') {
						this.hidePlayPause = !this.hidePlayPause;
						this.hideControl = false;
						if (this.playPauseTimeout) {
							clearTimeout(this.playPauseTimeout);
						}
						if (this.isPlaying) {
							this.playPauseTimeout = setTimeout(() => {
								this.hidePlayPause = true;
								this.hideControl = true;
							}, 3000);
						}
					} else if (action === 'icon') {
						this.hidePlayPause = true;
						if (video.paused || video.ended) {
							this.isPlaying = true;
							// this.renderer2.setStyle(this.videoBlock.nativeElement, 'cursor', 'pointer');
							this.timeoutManage();
							video.play();
						} else {
							this.isPlaying = false;
							// this.renderer2.setStyle(this.videoBlock.nativeElement, 'cursor', 'pointer');
							this.hideControl = false;
							clearTimeout(this.timeout);
							video.pause();
						}
					}
				}
			}
		}
	}

	togglePlayForMobile(action) {
		if (!this.isDesktop) {
			this.togglePlay(action);
		}
	}

	overlayForward() {
		if (navigator.onLine) {
			const video = this.videoNativeEl;
			if (!this.isDesktop && video.currentTime !== video.duration) {
				this.touchCount++;
				setTimeout(() => {
					if (this.touchCount > 1) {
						this.touchCount = 0;
						this.hideSkipForward = false;
						this.forward();
						if (this.isPlaying) {
							this.hidePlayPause = true;
						} else {
							this.hidePlayPause = false;
						}
					} else {
						this.touchCount = 0;
					}
				}, 200);
			}
		}
	}

	overlayBackward() {
		if (navigator.onLine) {
			const video = this.videoNativeEl;
			if (!this.isDesktop && video.currentTime !== 0) {
				this.touchCount++;
				setTimeout(() => {
					if (this.touchCount > 1) {
						this.touchCount = 0;
						this.hideSkipBackward = false;
						this.backward();
						if (this.isPlaying) {
							this.hidePlayPause = true;
						} else {
							this.hidePlayPause = false;
						}
					} else {
						this.touchCount = 0;
					}
				}, 200);
			}
		}
	}

	doubleClick() {
		if (navigator.onLine) {
			if (this.options.fullscreen && this.isDesktop && this.loaded) {
				this.preventSimpleClick = true;
				clearTimeout(this.clickTimeout);
				this.toggleScreen();
			}
		}
	}

	formatTime(timeInSeconds, timeFormat = 'h:m:ss') {
		// Hours, minutes and seconds
		const hrs = ~~(timeInSeconds / 3600);
		const mins = ~~((timeInSeconds % 3600) / 60);
		const secs = ~~timeInSeconds % 60;
		const formats = timeFormat.split(':');

		let ret = '';
		if (hrs > 0) {
			const hours = (formats.includes('hh')) ? ((hrs < 10) ? `0${hrs}` : hrs) : hrs;
			ret += '' + hours + ':';
		} else if (formats.includes('hh')) {
			ret += '00:';
		}

		const minutes = (formats.includes('mm')) ? ((mins < 10) ? `0${mins}` : mins) : mins;

		const seconds = (formats.includes('ss')) ? ((secs < 10) ? `0${secs}` : secs) : secs;

		ret += '' + minutes + ':' + seconds;

		return ret;
	}

	updateTimeElapsed(event) {
		const time = this.formatTime(Math.round(this.videoNativeEl.currentTime));
		const formattedTime = this.formatTime(Math.round(this.videoNativeEl.currentTime), 'hh:mm:ss');
		if (this.segmentsProcess) {
			this.processSegment(formattedTime);
		}
		const timeInSeconds = this.gfService.getTimeToSeconds(formattedTime, { timeFormat: 'hh:mm:ss' });
		for (const timeData of this.trackingEmit) {
			const formattedTimeInSeconds = this.gfService.getTimeToSeconds(timeData.time, { timeFormat: 'hh:mm:ss' });
			if (!timeData.isPlayed && (formattedTime === timeData.time || formattedTimeInSeconds <= timeInSeconds)) {
				timeData.isPlayed = true;
				this.timeEmit.emit(timeData);
			}
		}
		if (this.bookMarksData[this.currentBookmarkIndex] && this.showBookmarkPagination) {
			this.bookMarksData[this.currentBookmarkIndex].timelineData.map((data, index) => {
				if (data.duration <= this.videoNativeEl.currentTime) {
					this.currentTimelineIndex = index;
				}
			})
		}
		let progress = this.videoNativeEl.currentTime / this.videoNativeEl.duration;
		if (progress < 0) progress = 0;
		this.renderer2.setStyle(this.playerProgressNativeEl, 'transform', `scaleX(${progress})`);
		const playerProgressBounds = this.playerProgressNativeEl.getBoundingClientRect();
		this.renderer2.setStyle(this.playerPointerNativeEl, 'transform', `translateX(${playerProgressBounds.width}px)`);
		this.loader = false;
		if (this.videoNativeEl.ended) {
			this.isPlaying = false;
			this.hideControl = false;
			if (!this.isDesktop) {
				this.hidePlayPause = false;
			}
		}
		this.updatedTime.emit(this.videoNativeEl.currentTime);
		this.videoElapsedTime = time;
	}

	processSegment(currentTime) {
		const index = this.segmentData.findIndex(items => items.start <= this.videoNativeEl.currentTime && items.end >= this.videoNativeEl.currentTime);
		if (index !== -1 && ([null, undefined].includes(this.oldSegmentIndex) || this.oldSegmentIndex !== index)) {
			this.segmentedData.push(index + 1);
			this.segmentedData.sort();
			this.segments.emit(
				{
					totalSegment: this.totalSegments,
					segments: this.segmentedData,
					currentSegment: index + 1,
					currentTime
				}
			);
			this.oldSegmentIndex = index;
		}
	}

	resetPlayer() {
		this.isPlaying = false;
		this.renderer2.setStyle(this.playerProgressNativeEl, 'transform', `scaleX(0)`);
	}

	replay() {
		if (navigator.onLine) {
			const video = this.videoNativeEl;
			this.renderer2.setStyle(this.playerProgressNativeEl, 'transform', `scaleX(0)`);
			video.play();
			this.isPlaying = true;
		}
	}

	bufferHandler(event) {
		const video = this.videoNativeEl;
		if (video.duration > 0) {
			for (var i = 0; i < video.buffered.length; i++) {
				if (video.buffered.start(video.buffered.length - 1 - i) < video.currentTime) {
					const bufferPercentage = video.buffered.end(video.buffered.length - 1 - i) / video.duration;
					this.renderer2.setStyle(this.bufferNativeEl, 'transform', `scaleX(${bufferPercentage})`);
					break;
				}
			}
		}
	}

	seekBar(event) {
		if (event.clientX) {
			// let seekBarBounds = this.seekBarNativeEl.getBoundingClientRect();
			// let shiftX = event.clientX - playerPointerBounds.left;
			// let posX = event.clientX - shiftX - seekBarBounds.left;
			let posX = event.clientX - this.seekBarBounds.left;
			if (posX < 0) {
				posX = 0;
			}
			if (posX > this.seekBarNativeEl.offsetWidth) {
				posX = this.seekBarNativeEl.offsetWidth;
			}
			let progress = posX / this.seekBarNativeEl.offsetWidth;
			if (progress < 0) progress = 0;
			this.renderer2.setStyle(this.playerPointerNativeEl, 'transform', `translateX(${posX}px)`);
			this.renderer2.setStyle(this.playerProgressNativeEl, 'transform', `scaleX(${progress})`);
			this.renderer2.setStyle(this.progressTooltipNativeEl, 'left', `${progress * 100}%`);
			const currentTime = progress * this.videoNativeEl.duration;
			const time = this.formatTime(currentTime);
			if (time !== this.duration) {
				this.duration = time;
				this.videoElapsedTime = time;
			}
			this.videoNativeEl.currentTime = currentTime;
		}
	}

	skip(video) {
		let progress = Math.floor(video.currentTime / video.duration);
		if (progress < 0) progress = 0;
		this.renderer2.setStyle(this.playerProgressNativeEl, 'transform', `scaleX(${progress})`);
	}

	forward() {
		if (navigator.onLine) {
			const video = this.videoNativeEl;
			if (video.duration !== video.currentTime) {
				this.loader = true;
				video.currentTime += 10;
				const time = this.formatTime(video.currentTime);
				this.videoElapsedTime = time;
				this.skip(video);
			} else {
				this.resetPlayer();
			}
		}
	}

	backward() {
		if (navigator.onLine) {
			const video = this.videoNativeEl;
			if (video.currentTime !== 0) {
				this.loader = true;
				video.currentTime -= 10;
				const time = this.formatTime(video.currentTime);
				this.videoElapsedTime = time;
				this.skip(video);
			} else {
				this.resetPlayer();
			}
		}
	}

	mute() {
		const video = this.videoNativeEl;

		this.volumeBarBounds = this.volumeBarNativeEl.getBoundingClientRect();

		video.muted = !video.muted;
		if (video.muted) {
			this.renderer2.setStyle(this.volumeProgressNativeEl, 'height', `0px`);
			this.renderer2.setStyle(this.volumePointerNativeEl, 'transform', `translateY(0px)`);
		} else {
			const posY = this.volumeBarNativeEl.offsetHeight * this.currentVolume;
			this.renderer2.setStyle(this.volumeProgressNativeEl, 'height', `${posY}px`);
			this.renderer2.setStyle(this.volumePointerNativeEl, 'transform', `translateY(-${posY}px)`);
		}
	}

	toggleScreen() {
		// if (this.isDesktop) {
		const actualEl = this.hostElement.nativeElement;
		const mainEl: HTMLElement = window.document.getElementById('main-container');
		if (!this.fullscreen) {
			if (!this.isDesktop) {
				if (screen && screen.orientation && (screen.orientation.type === 'portrait-primary' || screen.orientation.type === 'portrait-secondary')) {
					if (mainEl) {
						this.renderer2.setStyle(mainEl, 'max-width', `${window.innerWidth || document.documentElement.clientWidth}px`);
						this.renderer2.setStyle(mainEl, 'min-width', 'auto');
					}
				}
			}
			let fullScreenMethod;
			if (actualEl.requestFullscreen) {
				fullScreenMethod = actualEl.requestFullscreen();
			} else if (actualEl.mozRequestFullScreen) {
				fullScreenMethod = actualEl.mozRequestFullScreen();
			} else if (actualEl.webkitRequestFullscreen) {
				fullScreenMethod = actualEl.webkitRequestFullscreen();
			} else if (actualEl.msRequestFullscreen) {
				fullScreenMethod = actualEl.msRequestFullscreen();
			}
			if (fullScreenMethod) {
				fullScreenMethod.then(data => {
					if (!this.isDesktop && screen && screen.orientation) {
						this.gfService.globalBS$.next(
							{
								type: 'FULL_SCREEN',
								value: true
							}
						);
						setTimeout(() => {
							screen.orientation.lock('landscape');
						});
					}
				});
			}
			if (this.isPlaying) {
				this.timeoutManage();
			}
		} else {
			if (mainEl) {
				this.renderer2.setStyle(mainEl, 'min-width', 'auto');
				this.renderer2.setStyle(mainEl, 'max-width', '100%');
			}
			let exitFullscreenMethod;
			if (document.exitFullscreen) {
				exitFullscreenMethod = document.exitFullscreen();
			} else if (document['mozCancelFullScreen']) {
				exitFullscreenMethod = document['mozCancelFullScreen']();
			} else if (document['webkitExitFullscreen']) {
				exitFullscreenMethod = document['webkitExitFullscreen']();
			} else if (document['msExitFullscreen']) {
				exitFullscreenMethod = document['msExitFullscreen']();
			}
			if (exitFullscreenMethod) {
				exitFullscreenMethod.then(data => {
					if (!this.isDesktop && screen && screen.orientation) {
						this.gfService.globalBS$.next(
							{
								type: 'FULL_SCREEN',
								value: false
							}
						);
						screen.orientation.unlock();
					}
				});
			}
			if (this.timeout) {
				// clearTimeout(this.timeout);
			}
			this.hideControl = false;
		}
		this.fullscreen = !this.fullscreen;
		// }
	}

	timeoutManage(el: HTMLElement = null) {
		let status = false;
		if (el) {
			if (this.videoBlock.nativeElement.contains(el)) {
				status = true;
			}
		} else {
			status = true;
		}
		if (status) {
			clearTimeout(this.timeout);
			this.timeout = setTimeout(() => {
				if (!this.videoNativeEl.ended) {
					this.hideControl = true;
					this.renderer2.setStyle(this.videoBlock.nativeElement, 'cursor', 'none');
				}
			}, 3000);
		}
	}

	leaveControl() {
		if (this.isPlaying) {
			clearTimeout(this.timeout);
			this.timeout = setTimeout(() => {
				if (!this.videoNativeEl.ended) {
					this.hideControl = true;
					this.renderer2.setStyle(this.videoBlock.nativeElement, 'cursor', 'none');
				}
			}, 3000);
		}
	}

	overOnControl() {
		if (this.timeout) {
			clearTimeout(this.timeout);
		}
	}

	onDragStart() {
		this.dragState = true;
		if (!this.playStatus) {
			if (this.videoNativeEl.paused) {
				this.playStatus = false;
			} else {
				this.playStatus = true;
				this.videoNativeEl.pause();
			}
		} else {
			if (!this.videoNativeEl.paused) {
				this.videoNativeEl.pause();
			}
		}

		this.isPlaying = false;
	}
	onDrag(event: DragEvent) {
		this.loader = true;
		const progressTooltipCSS = getComputedStyle(this.progressTooltipNativeEl);
		const playerPointerCSS = getComputedStyle(this.playerPointerNativeEl);
		if (progressTooltipCSS.display === 'none')
			this.renderer2.setStyle(this.progressTooltipNativeEl, 'display', `flex`);
		if (playerPointerCSS.opacity === '0') this.renderer2.setStyle(this.playerPointerNativeEl, 'opacity', `1`);
		this.seekBar(event);
	}
	onDragEnd(event) {
		this.dragState = false;
		this.documentListenMU();
		this.documentListenMM();
		if (this.playStatus) {
			this.isPlaying = true;
			this.playStatus = false;
			this.videoNativeEl.play();
		}
		if (!this.progressBarState) {
			this.renderer2.setStyle(this.progressTooltipNativeEl, 'display', `none`);
			this.renderer2.setStyle(this.playerPointerNativeEl, 'opacity', `0`);
		}
	}

	@HostListener('contextmenu', ['$event'])
	preventContext(event) {
		event.preventDefault();
	}

	@HostListener('mousemove', ['$event'])
	onMouseMove(e) {
		if (this.loaded) {
			const el: HTMLElement = e.target;
			if (this.isPlaying) {
				this.hideControl = false;
				this.renderer2.setStyle(this.videoBlock.nativeElement, 'cursor', 'pointer');
				// clearTimeout(this.timeout);
				this.timeoutManage(el);
			} else {
				this.renderer2.setStyle(this.videoBlock.nativeElement, 'cursor', 'pointer');
			}
		}
	}

	onProgressBarMouseMove(event: MouseEvent) {
		if (!this.volumeDragState) {
			// let seekBarBounds = this.seekBarNativeEl.getBoundingClientRect();
			// let shiftX = event.clientX - playerPointerBounds.left;
			// let posX = event.clientX - shiftX - seekBarBounds.left;
			let posX = event.clientX - this.seekBarBounds.left;
			if (posX < 0) posX = 0;
			if (posX > this.seekBarNativeEl.offsetWidth) {
				posX = this.seekBarNativeEl.offsetWidth;
			}
			let progress = posX / this.seekBarNativeEl.offsetWidth;
			if (progress < 0) progress = 0;
			const progressTooltipCSS = getComputedStyle(this.progressTooltipNativeEl);
			const playerPointerCSS = getComputedStyle(this.playerPointerNativeEl);
			if (playerPointerCSS.opacity === '0') this.renderer2.setStyle(this.playerPointerNativeEl, 'opacity', `1`);
			if (progressTooltipCSS.display === 'none')
				this.renderer2.setStyle(this.progressTooltipNativeEl, 'display', `flex`);
			this.renderer2.setStyle(this.progressTooltipNativeEl, 'left', `${progress * 100}%`);
			const currentTime = progress * this.videoNativeEl.duration;
			const time = this.formatTime(currentTime);
			this.duration = `${time}`;
		}
	}

	onProgressBarMouseOver(event) {
		if (!this.volumeDragState) {
			this.progressBarState = true;
			this.renderer2.setStyle(this.progressTooltipNativeEl, 'display', `flex`);
			this.renderer2.setStyle(this.playerPointerNativeEl, 'opacity', `1`);
		}
	}
	onProgressBarMouseLeave(event) {
		this.progressBarState = false;
		if (!this.dragState) {
			this.renderer2.setStyle(this.progressTooltipNativeEl, 'display', `none`);
			this.renderer2.setStyle(this.playerPointerNativeEl, 'opacity', `0`);
		}
	}

	pointerMouseDown(event) {
		event.preventDefault();
		this.onDragStart();
		this.documentListenMM = this.renderer2.listen(document, 'mousemove', this.onDrag.bind(this));
		this.documentListenMU = this.renderer2.listen(document, 'mouseup', this.onDragEnd.bind(this));
	}

	seekBarMouseDown(event) {
		this.loader = true;
		this.seekBar(event);
		this.pointerMouseDown(event);
	}

	volumeControl(event) {
		if (event) {
			this.volumeBarBounds = this.volumeBarNativeEl.getBoundingClientRect();
			let posY = this.volumeBarNativeEl.offsetHeight - (event.clientY - this.volumeBarBounds.top);
			if (posY < 0) posY = 0;
			if (posY > this.volumeBarNativeEl.offsetHeight) {
				posY = this.volumeBarNativeEl.offsetHeight;
			}
			const scrubTime = posY / this.volumeBarNativeEl.offsetHeight;
			let volumePercentage = Math.floor(scrubTime / 1 * 100);
			if (volumePercentage < 0) {
				volumePercentage = 0;
			}
			this.renderer2.setStyle(this.volumeProgressNativeEl, 'height', `${posY}px`);
			this.renderer2.setStyle(this.volumePointerNativeEl, 'transform', `translateY(-${posY}px)`);
			if (volumePercentage >= 0 && volumePercentage <= 100) {
				this.videoNativeEl.volume = volumePercentage / 100;
				this.currentVolume = this.videoNativeEl.volume;
				if (this.videoNativeEl.volume == 0) {
					this.videoNativeEl.muted = true;
				} else {
					this.videoNativeEl.muted = false;
				}
			}
		}
	}
	onVolumeMouseOver(event) {
		if (!this.dragState) {
			this.volumeBarState = true;
			const volumeControlCSS = getComputedStyle(this.volumeControlNativeEl);
			if (volumeControlCSS.visibility === 'hidden')
				this.renderer2.setStyle(this.volumeControlNativeEl, 'visibility', `visible`);
		}
	}
	onVolumeMouseLeave(event) {
		this.volumeBarState = false;
		const volumeBarCSS = getComputedStyle(this.volumeControlNativeEl);
		if (volumeBarCSS.visibility === 'visible')
			this.renderer2.setStyle(this.volumeControlNativeEl, 'visibility', `hidden`);
	}
	volumePointerMouseDown(event) {
		event.preventDefault();
		this.onVolumeDragStart();
		this.documentListenMM = this.renderer2.listen(document, 'mousemove', this.onVolumeDrag.bind(this));
		this.documentListenMU = this.renderer2.listen(document, 'mouseup', this.onVolumeDragEnd.bind(this));
	}

	onVolumeDragStart() {
		this.volumeDragState = true;
	}
	onVolumeDrag(event: DragEvent) {
		const volumeBarCSS = getComputedStyle(this.volumeControlNativeEl);
		if (volumeBarCSS.visibility === 'hidden')
			this.renderer2.setStyle(this.volumeControlNativeEl, 'visibility', `visible`);
		this.volumeControl(event);
	}
	onVolumeDragEnd(event) {
		this.volumeDragState = false;
		this.documentListenMU();
		this.documentListenMM();
		const volumeBarCSS = getComputedStyle(this.volumeControlNativeEl);
		if (!this.volumeBarState) {
			if (volumeBarCSS.visibility === 'visible')
				this.renderer2.setStyle(this.volumeControlNativeEl, 'visibility', `hidden`);
		}
	}

	ngOnDestroy() {
		if (this.subscribe) {
			this.subscribe.unsubscribe();
		}
	}
}
